import { useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import {
    GetMicrostoreSitesQuery,
    MicrostoreColumnTemplateType,
    useReplaceMicrostoreColumnMutation,
    useGetMicrostoreColumnTemplatesQuery,
    MicrostoreColumnTemplateState,
} from 'data/__generated__';

import { Loader } from 'components/Loader';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemSelect } from 'components/TotemSelect';
import { TotemDatePicker } from 'components/TotemDatePicker';
import { dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';
import { NayaxMDBTemplateSelect } from 'components/Select/NayaxMDBTemplateSelect';

export function ReplaceMicrostoreColumnPopup({
    isOpen,
    setMicrostoreColumnToReplace,
    column,
}: {
    isOpen: boolean;
    setMicrostoreColumnToReplace: (
        column:
            | GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number]
            | null,
    ) => void;
    column: GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number];
}) {
    const [replacementDate, setReplacementDate] = useState<Date | null>(
        column.removalDate ? dateFromString(column.removalDate, true) : null,
    );
    const [microstoreColumnTemplateId, setMicrostoreColumnTemplateId] = useState<string | null>(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

    const {
        loading: microstoreColumnTemplateLoading,
        data: microstoreColumnTemplateData,
        error: microstoreColumnTemplateError,
    } = useGetMicrostoreColumnTemplatesQuery();

    const [replaceMicrostoreColumn, { loading: replacementLoading }] = useReplaceMicrostoreColumnMutation();

    // only display warnings for columns with fresh products
    const shouldDisplayDelaysWarning = column.type === MicrostoreColumnTemplateType.Fridge;

    const mondayInThreeWeeks = new Date();
    mondayInThreeWeeks.setDate(mondayInThreeWeeks.getDate() + 21 - ((mondayInThreeWeeks.getDay() + 6) % 7));
    mondayInThreeWeeks.setHours(0, 0, 0, 0);

    if (microstoreColumnTemplateLoading) {
        return (
            <TotemPopup
                title="Ajouter une colonne"
                isOpen={isOpen}
                setIsOpen={() => setMicrostoreColumnToReplace(null)}
                contentOverflow="visible"
            >
                <Loader />
            </TotemPopup>
        );
    }

    if (microstoreColumnTemplateError || !microstoreColumnTemplateData) {
        throw new Error('Une erreur est survenue lors de la récupération des templates de colonne microstore');
    }

    async function handleSubmit() {
        if (!microstoreColumnTemplateId || !replacementDate) {
            return;
        }
        if (column.startingDate && replacementDate <= dateFromString(column.startingDate, true)) {
            toast.error(
                <span>Vous ne pouvez pas mettre une date de retrait avant la date de début de la colonne</span>,
                {
                    autoClose: false,
                },
            );
            return;
        }

        if (shouldDisplayDelaysWarning && replacementDate && mondayInThreeWeeks > replacementDate) {
            toast.error(
                <span>
                    "Non respect des délais nécessaires pour les achats en vue du lancement - le restocking ne sera pas
                    automatique"
                </span>,
                {
                    autoClose: false,
                },
            );
        }

        const { data } = await replaceMicrostoreColumn({
            variables: {
                replaceMicrostoreColumnInput: {
                    columnId: column._id,
                    microstoreColumnTemplateId,
                    replacementDate: formatDateAsAnniversary({ dateTime: replacementDate, useNewFormat: true }),
                    nayaxMDBTemplateId: selectedTemplateId,
                },
            },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors du replacement du template de colonne');
        }

        const {
            replaceMicrostoreColumnMutation: { errors },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        }

        setMicrostoreColumnToReplace(null);
    }

    const { microstoreColumnTemplatesQuery: microstoreColumnTemplates } = microstoreColumnTemplateData;

    const microstoreColumnTemplateOptions =
        microstoreColumnTemplates
            .filter(
                (microstoreColumnTemplate) => microstoreColumnTemplate.state === MicrostoreColumnTemplateState.Regular,
            )
            .map(({ _id: microstoreColumnTemplateId, name }) => ({
                value: microstoreColumnTemplateId,
                label: name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

    return (
        <TotemPopup
            title="Remplacer une colonne"
            isOpen={isOpen}
            setIsOpen={() => setMicrostoreColumnToReplace(null)}
            contentOverflow="visible"
        >
            <SubmitContainer>
                <TotemDatePicker
                    label="Date de remplacement"
                    selected={replacementDate}
                    isClearable
                    onChange={(date) => {
                        if (shouldDisplayDelaysWarning && date && mondayInThreeWeeks > date) {
                            window.alert(
                                "Non respect de la règle :\n\nLa date de switch d'une colonne doit être renseignée strictement avant le lundi de la semaine en J-14 du switch pour permettre les achats.\n\nVeuillez valider avec data & supply sinon il n'y aura pas de restocking",
                            );
                        }
                        setReplacementDate(date);
                    }}
                />
                <TotemSelect
                    label="Template de colonne microstore"
                    placeholder="Sélectionner le template de colonnes"
                    value={microstoreColumnTemplateId}
                    options={microstoreColumnTemplateOptions}
                    onChange={(value) => {
                        if (value) {
                            setMicrostoreColumnTemplateId(value);
                        }
                    }}
                />
                <NayaxMDBTemplateSelect value={selectedTemplateId} onChange={setSelectedTemplateId} />
                <TotemPrimaryLoadingButton
                    loading={replacementLoading}
                    onClick={handleSubmit}
                    disabled={!microstoreColumnTemplateId || !replacementDate}
                    useIcon={false}
                    label="Confirmer"
                />
            </SubmitContainer>
        </TotemPopup>
    );
}

const SubmitContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 15px;

    > :not(:first-child) {
        margin-top: 15px;
    }
`;
